import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Configurar timeout más largo para conexiones lentas
axios.defaults.timeout = 30000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [trabajador, setTrabajador] = useState(null);
  const [authError, setAuthError] = useState(null);
  
  // Ref para evitar múltiples fetchUser simultáneos
  const isFetchingRef = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Si venimos de un callback de Google Auth (`#session_id=...` en la URL),
    // dejamos que `AuthCallback` haga el intercambio antes que corramos /me.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async (retryCount = 0) => {
    // Evitar múltiples llamadas simultáneas
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setAuthError(null);
      
      if (response.data.trabajador_social_id) {
        try {
          const trabResponse = await axios.get(`${API_URL}/trabajadores/${response.data.trabajador_social_id}`);
          setTrabajador(trabResponse.data);
        } catch (trabError) {
          // Si falla obtener el trabajador, no deslogueamos - solo logueamos el error
          console.warn('Error fetching trabajador data:', trabError);
          // El usuario sigue autenticado, solo no tiene datos de trabajador
        }
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      
      // Reintentar en caso de error de red (máximo 2 reintentos)
      if ((!error.response || error.code === 'ERR_NETWORK') && retryCount < 2) {
        console.log(`Reintentando verificación de sesión... (intento ${retryCount + 1})`);
        isFetchingRef.current = false;
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return fetchUser(retryCount + 1);
      }
      
      // Solo desloguear si es un error de autenticación (401/403)
      // No desloguear por errores de red temporales
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Token inválido o expirado, deslogueando...');
        setAuthError('Tu sesión ha expirado. Por favor iniciá sesión nuevamente.');
        logout();
      } else {
        // Error de red u otro - mantener el estado actual pero mostrar warning
        console.warn('Error de red al verificar sesión, manteniendo estado actual');
        setAuthError('Problema de conexión. Algunas funciones pueden no estar disponibles.');
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      
      if (userData.trabajador_social_id) {
        try {
          const trabResponse = await axios.get(`${API_URL}/trabajadores/${userData.trabajador_social_id}`);
          setTrabajador(trabResponse.data);
        } catch (trabError) {
          console.warn('Error fetching trabajador after login:', trabError);
          // No bloquear el login si falla obtener datos del trabajador
        }
      }
      
      return userData;
    } catch (error) {
      // Limpiar cualquier estado parcial en caso de error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      setTrabajador(null);
      throw error;
    }
  };

  /**
   * Login para cursantes externos del Campus.
   * El JWT tiene tipo "externo_campus" y get_current_user lo resuelve
   * contra usuarios_externos_campus. Actualiza el estado de AuthContext
   * sin necesidad de reload para que ProtectedRoute reconozca la sesión.
   */
  const loginExterno = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/campus/auth/login-externo`, { email, password });
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      // Setear user mínimo para que isAuthenticated sea true de inmediato.
      // El useEffect [token] llamará a /auth/me y refrescará con rol=externo_campus.
      setUser({ id: userData.id, email: userData.email, rol: 'externo_campus' });

      return userData;
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      throw error;
    }
  };

  const register = async (email, password, trabajadorSocialId) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email,
      password,
      trabajador_social_id: trabajadorSocialId
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    if (userData.trabajador_social_id) {
      const trabResponse = await axios.get(`${API_URL}/trabajadores/${userData.trabajador_social_id}`);
      setTrabajador(trabResponse.data);
    }
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setTrabajador(null);
  };

  const updateTrabajador = async (updateData = null) => {
    // Verificar que hay usuario autenticado
    if (!user?.trabajador_social_id) {
      console.error('updateTrabajador: No hay trabajador_social_id disponible');
      throw new Error('No autenticado - por favor volvé a iniciar sesión');
    }
    
    try {
      // Si se pasan datos para actualizar, hacer PUT
      if (updateData) {
        await axios.put(`${API_URL}/trabajadores/${user.trabajador_social_id}`, updateData);
      }
      // Siempre refrescar los datos después
      const response = await axios.get(`${API_URL}/trabajadores/${user.trabajador_social_id}`);
      setTrabajador(response.data);
      return response.data;
    } catch (error) {
      console.error('updateTrabajador error:', error);
      throw error;
    }
  };

  // Verificar si el trabajador tiene todos los datos obligatorios completos
  const checkDatosCompletos = (trab) => {
    if (!trab) return true; // Si no hay trabajador vinculado, no aplica
    
    const camposBasicos = [
      trab.numero_matricula,
      trab.documento,
      trab.apellido,
      trab.nombre,
      trab.fecha_nacimiento,
      trab.titulo,
      trab.email,
      trab.celular,
      trab.domicilio_real?.calle,
      trab.domicilio_real?.numero,
      trab.domicilio_real?.localidad
    ];
    
    // Si tiene empleo, verificar también domicilio laboral
    if (!trab.sin_empleo) {
      camposBasicos.push(
        trab.domicilio_laboral?.calle,
        trab.domicilio_laboral?.numero,
        trab.domicilio_laboral?.localidad
      );
    }
    
    return camposBasicos.every(campo => campo && String(campo).trim() !== '');
  };

  const tieneDatosCompletos = trabajador ? checkDatosCompletos(trabajador) : true;

  // Función para limpiar el error de auth
  const clearAuthError = () => setAuthError(null);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      trabajador,
      loading,
      login,
      loginExterno,
      register,
      logout,
      updateTrabajador,
      isAdmin: user?.rol === 'admin' || user?.rol === 'administracion' || user?.rol === 'superadmin',
      isSuperAdmin: user?.rol === 'superadmin',
      userRole: user?.rol,
      isAuthenticated: !!user,
      tieneDatosCompletos,
      checkDatosCompletos,
      authError,
      clearAuthError
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};
